import { useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from '@apollo/client'

import Button from 'shared/ui/Button_2'
import Flex from 'shared/ui/Flex'
import Input from 'shared/ui/Input'
import Select from 'shared/ui/Select'
import IslandModal from 'shared/components/modals/IslandModal'
import { ADD_PAGES_TO_PROJECT } from 'data/layoutcreator/mutations/migrations'

// Page types array based on the existing system
const pageTypes = [
  'blank',
  'profile', 
  'collages',
  'quotes',
  'rankings',
  'reports',
  'other'
]

interface AddPagesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddPagesModal: React.FC<AddPagesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [projectId, setProjectId] = useState('')
  const [documentId, setDocumentId] = useState('')
  const [index, setIndex] = useState('')
  const [templatePath, setTemplatePath] = useState('')
  const [projectFolderName, setProjectFolderName] = useState('')
  const [selectedTypeIndex, setSelectedTypeIndex] = useState(0)
  
  const [addPages] = useMutation(ADD_PAGES_TO_PROJECT)

  const handleAddPages = async () => {
    if (!projectId || !documentId || !index) {
      toast.error('Project ID, Document ID, and Index are required')
      return;
    }

    const indexNumber = parseInt(index)
    if (isNaN(indexNumber) || indexNumber < 0) {
      toast.error('Index must be a valid number')
      return;
    }

    const toastId = toast.loading('Adding pages...')

    try {
      const variables: any = { 
        projectId,
        documentId,
        index: indexNumber,
        type: pageTypes[selectedTypeIndex]
      }

      // Only include templatePath if it's provided
      if (templatePath.trim()) {
        variables.templatePath = templatePath
      }

      // Only include projectFolderName if it's provided
      if (projectFolderName.trim()) {
        variables.projectFolderName = projectFolderName
      }

      const { data, errors } = await addPages({
        variables
      })

      toast.dismiss(toastId)

      if (errors) {
        toast.error('Failed to add pages')
        return
      }

      if (data) {
        toast.success('Successfully added pages to project')
        onClose()
        setProjectId('')
        setDocumentId('')
        setIndex('')
        setTemplatePath('')
        setProjectFolderName('')
        setSelectedTypeIndex(0)
      }
    } catch (error: any) {
      toast.dismiss(toastId)
      toast.error(error.message || 'Failed to add pages')
    }
  }

  const isFormValid = projectId && documentId && index

  return (
    <IslandModal
      isOpen={isOpen}
      closeModal={onClose}
      width={600}
      title="Add Pages to Project"
    >
      <Flex direction="column">
        <Flex direction="column" rowGap={16}>
          <Input
            value={projectId}
            onChange={setProjectId}
            label="Project ID"
            placeholder="Enter project ID"
          />
          
          <Input
            value={documentId}
            onChange={setDocumentId}
            label="Document ID"
            placeholder="Enter document ID"
          />
          
          <Input
            value={index}
            onChange={setIndex}
            label="Index"
            placeholder="Enter page index (number)"
            type="number"
          />
          
          <Input
            value={templatePath}
            onChange={setTemplatePath}
            label="Template Path (optional)"
            placeholder="Enter template path (leave empty to use project's current template)"
          />
          
          <Input
            value={projectFolderName}
            onChange={setProjectFolderName}
            label="Project Folder Name (optional)"
            placeholder="Enter project folder name (leave empty to use project name from meta)"
          />
          
          <Flex direction="column">
            <label style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Page Type
            </label>
            <Select
              options={pageTypes}
              activeIndex={selectedTypeIndex}
              onChange={(_, index) => setSelectedTypeIndex(index)}
            />
          </Flex>
        </Flex>

        <Flex marginTop={46}>
          <Button
            intent="primary-brand-01"
            textId="Add Pages"
            size="lg"
            isDisabled={!isFormValid}
            onPress={handleAddPages}
          />
        </Flex>
      </Flex>
    </IslandModal>
  )
} 